import {
	DndContext,
	type DragEndEvent,
	PointerSensor,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { Choice } from "@memorang/types";
import { useCallback, useMemo } from "react";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../../../useAppTheme";
import { AppIcon } from "../../../AppIcon";
import Box from "../../../Box";
import { parseDragDropStemSegments } from "./parse-drag-drop-stem";

type Props = {
	stemText?: string;
	wordBank: Choice[];
	values: Record<string, string>;
	onChangeValues: (values: Record<string, string>) => void;
	disabled?: boolean;
	isAnswered?: boolean;
	inReviewMode?: boolean;
	correctWordIdsByBlank?: Record<string, string>;
	hidePlaceholder?: boolean;
};

const BLANK_PLACEHOLDER = "--------------";

const getChoiceById = (choices: Choice[], id?: string) =>
	choices.find((choice) => choice.id === id);

const DraggableWord = ({
	word,
	sourceBlankId,
	disabled,
}: {
	word: Choice;
	sourceBlankId?: string;
	disabled?: boolean;
}) => {
	const theme = useAppTheme();
	const { attributes, listeners, setNodeRef, transform, isDragging } =
		useDraggable({
			id: sourceBlankId ? `assigned:${sourceBlankId}` : `bank:${word.id}`,
			data: {
				wordId: word.id,
				sourceBlankId,
			},
			disabled,
		});

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(transform),
				opacity: isDragging ? 0.5 : 1,
				cursor: disabled ? "default" : "grab",
				backgroundColor: theme.colors.surface,
				border: `1px solid ${theme.colors.outline}`,
				borderRadius: theme.roundness * 2,
				padding: "8px 16px",
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				touchAction: "none",
			}}
			{...listeners}
			{...attributes}
		>
			<Text
				variant="bodyMedium"
				style={{
					fontSize: 16,
					lineHeight: 24,
					color: theme.colors.onSurface,
				}}
			>
				{word.text || word.id}
			</Text>
		</div>
	);
};

const GhostToken = ({ word }: { word: Choice }) => {
	const theme = useAppTheme();
	return (
		<div
			style={{
				backgroundColor: theme.colors.surfaceDisabled,
				borderRadius: theme.roundness * 2,
				padding: "8px 16px",
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Text
				variant="bodyMedium"
				style={{ fontSize: 16, lineHeight: 24, opacity: 0 }}
			>
				{word.text || word.id}
			</Text>
		</div>
	);
};

const DroppableBlank = ({
	blankId,
	word,
	disabled,
	showReviewState,
	isCorrect,
	onClear,
	width,
	hidePlaceholder,
}: {
	blankId: string;
	word?: Choice;
	disabled?: boolean;
	showReviewState: boolean;
	isCorrect: boolean;
	onClear: (blankId: string) => void;
	width: number;
	hidePlaceholder?: boolean;
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}) => {
	const theme = useAppTheme();
	const { setNodeRef, isOver } = useDroppable({
		id: `blank:${blankId}`,
		disabled,
	});

	const filled = Boolean(word);
	const canShowClose = filled && !disabled && !showReviewState;

	const reviewAnswer = isCorrect
		? theme.palette.answer.correct
		: theme.palette.answer.incorrect;

	const backgroundColor = showReviewState
		? word
			? reviewAnswer.background
			: theme.palette.answer.incorrect.background
		: theme.colors.surface;

	const borderColor = showReviewState
		? word
			? reviewAnswer.border
			: theme.palette.answer.incorrect.border
		: disabled
			? theme.colors.outlineVariant
			: filled
				? theme.colors.primary
				: theme.colors.outline;

	const textColor =
		showReviewState && word
			? theme.colors.onSurface
			: disabled || !filled
				? theme.colors.onSurfaceVariant
				: theme.colors.onSurface;

	const {
		attributes,
		listeners,
		setNodeRef: setDragRef,
		transform,
		isDragging,
	} = useDraggable({
		id: `assigned:${blankId}`,
		data: {
			wordId: word?.id ?? "",
			sourceBlankId: blankId,
		},
		disabled: disabled || !word || showReviewState,
	});

	const showCloseArea = !(disabled || showReviewState);

	return (
		<div
			ref={setNodeRef}
			style={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "space-between",
				width,
				height: 40,
				margin: "0 4px",
				border: `1px ${disabled || filled ? "solid" : "dashed"} ${borderColor}`,
				borderRadius: theme.roundness * 2,
				backgroundColor,
				paddingLeft: 16,
				paddingRight: showCloseArea ? 4 : 16,
				boxSizing: "border-box",
				verticalAlign: "middle",
				overflow: "hidden",
				boxShadow: isOver ? `0 0 0 2px ${theme.colors.primary}` : "none",
			}}
		>
			<div
				ref={filled ? setDragRef : undefined}
				style={{
					flex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: filled ? "flex-start" : "center",
					cursor: filled && !disabled && !showReviewState ? "grab" : "default",
					opacity: isDragging ? 0.5 : 1,
					transform: filled ? CSS.Translate.toString(transform) : undefined,
				}}
				{...(filled && !disabled && !showReviewState ? listeners : {})}
				{...(filled && !disabled && !showReviewState ? attributes : {})}
			>
				<Text
					variant="bodyMedium"
					style={{ fontSize: 16, lineHeight: 24, color: textColor }}
				>
					{filled && word
						? word.text || word.id
						: hidePlaceholder
							? ""
							: BLANK_PLACEHOLDER}
				</Text>
			</div>
			{showCloseArea ? (
				<button
					type="button"
					onClick={
						canShowClose
							? (e) => {
									e.stopPropagation();
									onClear(blankId);
								}
							: undefined
					}
					onPointerDown={(e) => e.stopPropagation()}
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						background: "transparent",
						border: "none",
						margin: 0,
						width: 32,
						height: 32,
						cursor: canShowClose ? "pointer" : "default",
						flexShrink: 0,
						visibility: canShowClose ? "visible" : "hidden",
					}}
					aria-label="Remove word"
				>
					<AppIcon
						name="close"
						size={16}
						color={theme.colors.onSurfaceVariant}
					/>
				</button>
			) : null}
		</div>
	);
};

const BankDropZone = ({
	children,
	disabled,
}: {
	children: React.ReactNode;
	disabled?: boolean;
}) => {
	const theme = useAppTheme();
	const { setNodeRef } = useDroppable({
		id: "bank",
		disabled,
	});

	return (
		<div
			ref={setNodeRef}
			style={{
				display: "flex",
				flexWrap: "wrap",
				gap: 16,
				padding: 16,
				borderRadius: theme.roundness * 2,
				backgroundColor: theme.colors.surfaceVariant,
			}}
		>
			{children}
		</div>
	);
};

const DragDropBlanks = ({
	stemText,
	wordBank,
	values,
	onChangeValues,
	disabled = false,
	isAnswered,
	inReviewMode,
	correctWordIdsByBlank,
	hidePlaceholder,
}: Props) => {
	const blankWidth = useMemo(() => {
		const PADDING_LEFT = 16;
		const PADDING_RIGHT = 4;
		const CLOSE_BUTTON_WIDTH = 32;
		const BORDER = 2;
		const MIN_TEXT_WIDTH = 80;

		let maxTextWidth = MIN_TEXT_WIDTH;
		if (typeof document !== "undefined") {
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (ctx) {
				ctx.font = "16px system-ui, sans-serif";
				for (const word of wordBank) {
					const w = ctx.measureText(word.text || word.id).width;
					if (w > maxTextWidth) {
						maxTextWidth = w;
					}
				}
			}
		}

		return Math.ceil(
			PADDING_LEFT + maxTextWidth + PADDING_RIGHT + CLOSE_BUTTON_WIDTH + BORDER,
		);
	}, [wordBank]);
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
	);

	const clearBlank = useCallback(
		(blankId: string) => {
			const nextValues = { ...values };
			delete nextValues[blankId];
			onChangeValues(nextValues);
		},
		[onChangeValues, values],
	);

	const segments = useMemo(
		() => (stemText ? parseDragDropStemSegments(stemText) : []),
		[stemText],
	);
	const assignedWordIdSet = useMemo(
		() => new Set(Object.values(values)),
		[values],
	);

	const handleDragEnd = useCallback(
		({ active, over }: DragEndEvent) => {
			if (!over) {
				return;
			}

			const activeData = active.data.current as
				| {
						wordId: string;
						sourceBlankId?: string;
				  }
				| undefined;

			if (!activeData) {
				return;
			}

			const nextValues = { ...values };
			const { wordId, sourceBlankId } = activeData;

			if (over.id === "bank") {
				if (sourceBlankId) {
					delete nextValues[sourceBlankId];
					onChangeValues(nextValues);
				}
				return;
			}

			if (typeof over.id === "string" && over.id.startsWith("blank:")) {
				const targetBlankId = over.id.replace("blank:", "");
				const previousBlankId = Object.entries(nextValues).find(
					([, assignedWordId]) => assignedWordId === wordId,
				)?.[0];

				if (previousBlankId) {
					delete nextValues[previousBlankId];
				}

				nextValues[targetBlankId] = wordId;
				onChangeValues(nextValues);
			}
		},
		[onChangeValues, values],
	);

	const showReviewState = Boolean(isAnswered || inReviewMode);

	return (
		<DndContext sensors={sensors} onDragEnd={handleDragEnd}>
			<Box gap={16}>
				<div style={{ fontSize: 16, lineHeight: "40px" }}>
					{segments.map((segment) => {
						if (segment.type === "text") {
							return (
								<Text
									key={segment.key}
									variant="bodyMedium"
									style={{ fontSize: 16, lineHeight: 40 }}
								>
									{segment.value.replace(/\n+/g, " ")}
								</Text>
							);
						}

						const assignedWordId = values[segment.blankId];
						const assignedWord = getChoiceById(wordBank, assignedWordId);

						return (
							<DroppableBlank
								key={segment.blankId}
								blankId={segment.blankId}
								word={assignedWord}
								disabled={disabled}
								showReviewState={showReviewState}
								isCorrect={
									assignedWordId != null &&
									correctWordIdsByBlank?.[segment.blankId] === assignedWordId
								}
								onClear={clearBlank}
								width={blankWidth}
								hidePlaceholder={hidePlaceholder}
							/>
						);
					})}
				</div>

				<BankDropZone disabled={disabled}>
					{wordBank.map((word) => {
						if (assignedWordIdSet.has(word.id)) {
							return <GhostToken key={word.id} word={word} />;
						}
						return (
							<DraggableWord
								key={word.id}
								word={word}
								disabled={disabled || showReviewState}
							/>
						);
					})}
				</BankDropZone>
			</Box>
		</DndContext>
	);
};

export default DragDropBlanks;
